/**
 * The copy a fresh install renders before anybody has opened the admin panel.
 *
 * Not placeholders: this is real, shippable text. A site whose database has no
 * `site_content` rows yet should look finished, because that is exactly the
 * state it is in the first time anyone loads it - and "Lorem ipsum" on a live
 * domain is worse than an opinion somebody can edit.
 *
 * Every value here is overridden the moment an admin saves the matching
 * section, per language.
 */

// Written the way a dealer would say it, not the way a contract would.
//
// Persian written for the web slides into the bookish register by default -
// «است»، «می‌شود»، «را»، «نمایید» - and next to a poker table that reads like
// terms and conditions rather than an invitation. So the whole of this block is
// second-person singular and spoken: «می‌شه» rather than «می‌شود»، «رو» rather
// than «را». The English below is untouched; English has no equivalent gap.
const fa = {
  hero: {
    eyebrow: 'پوکر آنلاین فارسی',
    headline: 'میز همیشه بازه',
    subheadline:
      'کش‌گیم و تورنمنت، با یه موتور بازی سریع و دست‌هایی که زنده پخش می‌شن. مرورگرت رو باز کن و بشین — نه اپ‌استوری، نه انتظاری.',
    primaryLabel: 'بازی در مرورگر',
    primaryHref: '/play/',
    secondaryLabel: 'ساخت حساب',
    secondaryHref: '/register',
  },
  features: {
    title: 'چرا اینجا',
    subtitle: 'چیزهایی که وقتی سر میز می‌شینی واقعاً حس می‌کنی.',
    items: [
      {
        icon: 'zap',
        title: 'بدون نصب',
        body: 'تو مرورگر باز می‌شه. رو آیفون هم مثل یه اپ می‌شینه رو صفحه‌ی خونه.',
      },
      {
        icon: 'shield',
        title: 'دست‌ها شفافه',
        body: 'تاریخچه‌ی هر دست ذخیره می‌شه و هر وقت خواستی می‌تونی برش گردونی.',
      },
      {
        icon: 'trophy',
        title: 'تورنمنت با برنامه',
        body: 'از ساتلایت تا تورنمنت‌های تضمینی، با ساختار بلایندی که از قبل معلومه.',
      },
      {
        icon: 'wallet',
        title: 'واریز و برداشت رمزارزی',
        body: 'چند تا شبکه، و کارمزد و حداقلش رو قبل از تأیید می‌بینی.',
      },
    ],
  },
  steps: {
    title: 'در سه قدم',
    items: [
      { title: 'حساب بساز', body: 'با ایمیل، کمتر از یه دقیقه.' },
      { title: 'موجودی اضافه کن', body: 'شبکه‌ات رو انتخاب کن و واریز کن.' },
      { title: 'بشین سر میز', body: 'کش‌گیم یا تورنمنت — هرکدوم باز بود.' },
    ],
  },
  faq: {
    title: 'سؤال‌هایی که زیاد می‌پرسن',
    items: [
      {
        question: 'رو آیفون چطور نصبش کنم؟',
        answer:
          'سایت رو تو Safari باز کن، دکمه‌ی اشتراک‌گذاری رو بزن و «Add to Home Screen» رو انتخاب کن. از اون به بعد مثل یه اپ باز می‌شه.',
      },
      {
        question: 'حتماً باید نصبش کنم؟',
        answer: 'نه. بازی تو همون مرورگر اجرا می‌شه؛ نصب فقط باز شدنش رو سریع‌تر می‌کنه.',
      },
    ],
  },
  install: {
    title: 'رو گوشیت نصبش کن',
    body: 'نه اپ‌استوری، نه منتظر تأیید موندنی. مستقیم از مرورگر.',
    iosSteps:
      '۱. سایت رو تو **Safari** باز کن\n۲. دکمه‌ی اشتراک‌گذاری رو بزن\n۳. **Add to Home Screen** رو انتخاب کن',
    androidSteps: '۱. سایت رو تو **Chrome** باز کن\n۲. منوی سه‌نقطه\n۳. **Install app**',
  },
  footer: {
    tagline: 'پوکر آنلاین، تو مرورگر.',
    copyright: '© شاه پوکر',
    responsibleTitle: 'بازی مسئولانه',
    // The one line where the voice tightens on purpose. Somebody reading this
    // may be reading it about themselves, and a chatty tone is the wrong thing
    // to hear at that moment.
    responsibleBody:
      'این بازی برای سرگرمیه. اگه حس می‌کنی داره از کنترلت خارج می‌شه، برای واریزت محدودیت بذار یا حسابت رو یه مدت ببند.',
    links: [
      { label: 'مجله', href: '/magazine', group: 'محتوا' },
      { label: 'قوانین', href: '/terms', group: 'قانونی' },
      { label: 'حریم خصوصی', href: '/privacy', group: 'قانونی' },
      { label: 'درباره ما', href: '/about', group: 'قانونی' },
    ],
  },
  seo: {
    // Search results and shared links are read cold, by people who have never
    // seen the site, so these stay plain rather than chatty.
    title: 'شاه پوکر — پوکر آنلاین در مرورگر',
    description: 'کش‌گیم و تورنمنت پوکر، بدون نصب. روی گوشی هم نصب می‌شود.',
    magazineTitle: 'مجله شاه پوکر',
    magazineDescription: 'استراتژی، تحلیل دست و اخبار تورنمنت‌ها.',
  },
  legal: {},
};
const en = {
  hero: {
    eyebrow: 'Online poker',
    headline: 'The table is always open',
    subheadline:
      'Cash games and tournaments, with a fast engine and full hand history. Open it in a browser, install it on your phone — no app store.',
    primaryLabel: 'Play in the browser',
    primaryHref: '/play/',
    secondaryLabel: 'Create an account',
    secondaryHref: '/register',
  },
  features: {
    title: 'Why here',
    subtitle: 'The things you actually notice once you sit down.',
    items: [
      {
        icon: 'zap',
        title: 'Nothing to install',
        body: 'It opens in the browser, and sits on the home screen like an app on iOS too.',
      },
      {
        icon: 'shield',
        title: 'Every hand on record',
        body: 'Hand histories are kept and can be replayed.',
      },
      {
        icon: 'trophy',
        title: 'Scheduled tournaments',
        body: 'Satellites through to guaranteed events, with a published blind structure.',
      },
      {
        icon: 'wallet',
        title: 'Crypto in and out',
        body: 'Several networks, with the minimum and the fee shown before you confirm.',
      },
    ],
  },
  steps: {
    title: 'Three steps',
    items: [
      { title: 'Create an account', body: 'With an email, in under a minute.' },
      { title: 'Add a balance', body: 'Pick your network and deposit.' },
      { title: 'Take a seat', body: 'Cash game or tournament, whichever is running.' },
    ],
  },
  faq: {
    title: 'Common questions',
    items: [
      {
        question: 'How do I install it on an iPhone?',
        answer:
          'Open the site in Safari, tap the share button, and choose “Add to Home Screen”. It opens like an app after that.',
      },
      {
        question: 'Do I have to install it to play?',
        answer: 'No. The game runs in the browser; installing just makes it quicker to open.',
      },
    ],
  },
  install: {
    title: 'Put it on your phone',
    body: 'No app store, no waiting for review. Straight from the browser.',
    iosSteps:
      '1. Open the site in **Safari**\n2. Tap the share button\n3. Choose **Add to Home Screen**',
    androidSteps: '1. Open the site in **Chrome**\n2. Three-dot menu\n3. **Install app**',
  },
  footer: {
    tagline: 'Online poker, in the browser.',
    copyright: '© Shah Poker',
    responsibleTitle: 'Play responsibly',
    responsibleBody:
      'This is a game. If it stops feeling like one, set a deposit limit or close your account for a while.',
    links: [
      { label: 'Magazine', href: '/magazine', group: 'Content' },
      { label: 'Terms', href: '/terms', group: 'Legal' },
      { label: 'Privacy', href: '/privacy', group: 'Legal' },
      { label: 'About', href: '/about', group: 'Legal' },
    ],
  },
  seo: {
    title: 'Shah Poker — online poker in your browser',
    description: 'Cash games and tournaments, nothing to install. Installable on your phone.',
    magazineTitle: 'Shah Poker Magazine',
    magazineDescription: 'Strategy, hand breakdowns and tournament news.',
  },
  legal: {},
};

const DEFAULTS = { fa, en };

/** Persian is the default language, so it is also the last fallback. */
export function defaultsFor(locale) {
  return DEFAULTS[locale] || DEFAULTS.fa;
}

export default DEFAULTS;
